import { useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAssignmentById,
  getDraftPages,
  getSubmissionsByAssignmentId,
  getCurrentSubmissionBatch,
} from '../service';
import { buildSnapshotResume, buildResume, FLOW_STEP } from '../components/BulkUpload/utils/draftResume';
import { pendingBatchKey, recoverPendingBatch } from '../components/BulkUpload/utils/pendingBatch';
import { createPendingBatchRetryPolicy, isPendingBatchBusy } from '../components/BulkUpload/utils/pendingBatchRetry';
import { isBatchResubmission } from '../components/BulkUpload/utils/feedbackRun';

/**
 * The read half of the draft-page snapshot, for a bulk upload that is attached to
 * an assignment from the start — the Tasks flow.
 *
 * `useDraftPageSync` writes the page list; this reads it back. Without the read
 * the write is pointless, which is why the Tasks flow shipped with neither and
 * lost every uploaded page on a refresh.
 *
 * Before commit, restore the snapshot. After commit, only the current batch's
 * submissions may resume the wizard; other task responses belong to their own flows.
 */

// Saves and batch commits can make the cached snapshot obsolete. Read again on
// every visit, but do not refresh and replace a wizard while it is being edited.
const RESUME_QUERY_OPTIONS = {
  staleTime: Infinity,
  refetchOnMount: 'always',
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
};

const UPLOADED_MANUAL_STATES = new Set(['SUBMITTED', 'FEEDBACK_ACCEPTED', 'REVIEWED']);

/**
 * @returns {{resume: ?Object, isLoading: boolean, isUnreadable: boolean}}
 *
 * `resume` is null until the snapshot is known, and stays null if it cannot be
 * read. An unreadable snapshot is NOT an empty one: handing the wizard an empty
 * page list would let autosave write it over whatever is really stored, so the
 * caller runs unpersisted instead.
 */
export const useDraftPageResume = (assignmentId, { enabled = true } = {}) => {
  const queryClient = useQueryClient();
  const isOn = Boolean(enabled && assignmentId);
  const pendingBatchRetry = useMemo(createPendingBatchRetryPolicy, [assignmentId]);
  // Another tab may clear the shared key; keep this read guarded until it settles.
  const pendingBatchRead = useMemo(() => ({ active: false }), [assignmentId]);
  const recoveryFailure = useRef(null);
  const { data, isLoading, isFetching, isError, error, failureReason, isFetchedAfterMount, refetch } = useQuery({
    queryKey: ['draftPages', assignmentId],
    queryFn: async () => {
      if (pendingBatchKey(assignmentId) || recoveryFailure.current?.assignmentId === assignmentId) {
        pendingBatchRead.active = true;
      }
      try {
        return await recoverPendingBatch(assignmentId, async (recovered) => {
          const snapshot = await getDraftPages(assignmentId);
          const active = recovered || await getCurrentSubmissionBatch(assignmentId);
          if (!active?.submissionIds?.length) return snapshot;
          const [all, assignment] = await Promise.all([
            getSubmissionsByAssignmentId(assignmentId),
            // The mounted flow reads this same key through useAssignmentById. Its
            // Continue action must use the same marking mode as this resume decision.
            queryClient.fetchQuery({
              queryKey: ['assignmentById', assignmentId],
              queryFn: () => getAssignmentById(assignmentId),
              staleTime: 0,
            }),
          ]);
          const targetIds = new Set(active.submissionIds);
          // Round 4c L2: which routed redos still await hand-in. An older server
          // leaves it out, and every routed RESUBMISSION_REQUESTED row then counts.
          const heldIds = Array.isArray(active.heldResubmissionIds)
            ? new Set(active.heldResubmissionIds) : undefined;
          const submissions = all.filter(row => targetIds.has(row?.id));
          const hasEveryTarget = submissions.length === targetIds.size &&
            new Set(submissions.map(row => row.id)).size === targetIds.size;
          const isManualTask = ['TEACHER', 'P2P'].includes(assignment?.reviewedBy);
          const isHeldRedo = (row) => isBatchResubmission(row, targetIds, undefined, heldIds);
          // An earlier class upload is finished once handed back to the student,
          // but a redo held by THIS batch must still resume for hand-in.
          const isReleasedClassRow = (row) => assignment?.type !== 'DOCUMENT' && !isHeldRedo(row) &&
            ['RESUBMISSION_REQUESTED', 'CLOSED'].includes(row.status);
          // Upload finishes at submission for manual/peer tasks; marking happens
          // outside this wizard. An incomplete read must retain the original batch.
          const manuallyUploaded = hasEveryTarget &&
            submissions.every(row => !isHeldRedo(row) &&
              (UPLOADED_MANUAL_STATES.has(row.status) || isReleasedClassRow(row)));
          // A reassigned student's row this batch routed the redo into keeps its
          // old attempt's markers until it leaves RESUBMISSION_REQUESTED (06 §5.8),
          // so it is never complete while it still is one.
          const uploadCompleted = isManualTask ? manuallyUploaded : !submissions.length ||
            submissions.every(row => !isHeldRedo(row) &&
              (isReleasedClassRow(row) || (row.jeddaiFeedbackReceivedAt && !row.jeddaiFailureReason)));
          if (uploadCompleted) return snapshot;
          const resume = buildResume({ submissions, snapshot, assignment, batchSubmissionIds: targetIds, heldResubmissionIds: heldIds });
          return { ...snapshot, recoveredResume: {
            ...resume,
            // Missing rows cannot turn a committed manual batch into a fresh upload.
            step: isManualTask && !hasEveryTarget ? FLOW_STEP.converting : FLOW_STEP[resume.step],
            submissionIds: [...targetIds],
          } };
        });
      } catch (error) {
        recoveryFailure.current = pendingBatchRead.active ? { assignmentId, error } : null;
        throw error;
      }
    },
    enabled: isOn,
    ...RESUME_QUERY_OPTIONS,
    ...pendingBatchRetry,
    onSuccess: () => { recoveryFailure.current = null; },
    onError: (error) => {
      if (pendingBatchRead.active) recoveryFailure.current = { assignmentId, error };
    },
    onSettled: () => { pendingBatchRead.active = false; },
  });

  // Cached data must not seed useState before this visit's read finishes. On a
  // failed refresh, the existing unpersisted fallback must not use the old version.
  const isLoadingResume = isOn && (isLoading || !isFetchedAfterMount ||
    (isFetching && (pendingBatchRead.active || Boolean(pendingBatchKey(assignmentId)))));
  const readableData = isOn && !isLoadingResume && !isError ? data : null;
  return {
    resume: readableData?.recoveredResume || (readableData ? buildSnapshotResume(readableData) : null),
    isLoading: isLoadingResume,
    isUnreadable: isError,
    isBatchSaving: isLoadingResume && isPendingBatchBusy(failureReason),
    isRecoveryError: isError && recoveryFailure.current?.assignmentId === assignmentId &&
      recoveryFailure.current?.error === error,
    refetch,
  };
};
